/**
 * GenerateControlCardUseCase
 * 
 * Main use case for generating control card PDFs.
 * Orchestrates all layers: Repository → Template → Output
 */

import { SupabaseClient } from '@supabase/supabase-js'
import { ControlCardEquipmentRepository, type ControlCardData } from '../../infrastructure'
import { PrototypeControlCardTemplate } from '../../presentation'
import type {
  GenerateControlCardRequest,
  GenerateControlCardResponse,
  OutputFormat,
} from '../dtos/ControlCardDTOs'
import { resolveNdtDateOverride, withNdtDateOverride } from '../ndtDateOverride'

/**
 * Use case for generating control card PDFs
 */
export class GenerateControlCardUseCase {
  private readonly repository: ControlCardEquipmentRepository
  private readonly template: PrototypeControlCardTemplate

  constructor(supabaseClient: SupabaseClient) {
    this.repository = new ControlCardEquipmentRepository(supabaseClient)
    this.template = new PrototypeControlCardTemplate()
  }

  /**
   * Execute the use case to generate a control card
   */
  async execute(
    request: GenerateControlCardRequest
  ): Promise<GenerateControlCardResponse> {
    try {
      // 1. Fetch equipment data
      const equipmentResult = await this.repository.getCompleteControlCardData(
        request.equipmentId
      )

      if (!equipmentResult.success) {
        return {
          success: false,
          error: `Failed to fetch equipment: ${equipmentResult.error}`,
        }
      }

      // 2. Prepare control card data
      // Filter sub-equipment based on includeSubEquipment option (default: true)
      const includeSubEquipment = request.options?.includeSubEquipment !== false
      const ndtDateOverride = resolveNdtDateOverride(request)

      const controlCardData: ControlCardData = {
        mainEquipment: withNdtDateOverride(equipmentResult.data.mainEquipment, ndtDateOverride),
        subEquipment: includeSubEquipment
          ? equipmentResult.data.subEquipment.map(eq => withNdtDateOverride(eq, ndtDateOverride))
          : [],
        inspection: request.inspection,
      }

      // 3. Render PDF
      const pdf = await this.template.renderComplete(
        controlCardData,
        request.logos,
        {
          finalize: request.options?.finalize ?? false,
          inspectionId: request.options?.inspectionId,
          attachmentImage: request.options?.attachmentImage,
        }
      )

      // 4. Get report number from inspection data
      const reportNumber = controlCardData.inspection.reportNumber || 'N/A'

      // 5. Prepare response
      const response: GenerateControlCardResponse = {
        success: true,
        pdf,
        blob: this.template.getBlob(),
        dataUrl: this.template.getDataUrl(),
        reportNumber,
        metadata: {
          equipmentName: controlCardData.mainEquipment.name || 'Unknown',
          subEquipmentCount: controlCardData.subEquipment.length,
          specificationCount: controlCardData.mainEquipment.specifications.count(),
          generatedAt: new Date(),
        },
      }

      return response
    } catch (error) {
      console.error('❌ Error generating control card:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      }
    }
  }

  /**
   * Generate and save control card to file
   */
  async generateAndSave(
    request: GenerateControlCardRequest,
    filename: string
  ): Promise<GenerateControlCardResponse> {
    const response = await this.execute(request)

    if (response.success && response.pdf) {
      this.template.save(filename)
    }

    return response
  }

  /**
   * Generate control card with specific output format
   */
  async generateWithFormat(
    request: GenerateControlCardRequest,
    format: OutputFormat
  ): Promise<GenerateControlCardResponse> {
    const response = await this.execute(request)

    if (!response.success) {
      return response
    }

    // Ensure the requested format is in the response
    switch (format) {
      case 'pdf':
        // Already in response
        break
      case 'blob':
        // Already in response
        break
      case 'dataurl':
        // Already in response
        break
      case 'arraybuffer':
        if (response.pdf) {
          response.blob = new Blob([this.template.getArrayBuffer()], {
            type: 'application/pdf',
          })
        }
        break
    }

    return response
  }

  /**
   * Preview control card (with watermark)
   */
  async preview(
    request: GenerateControlCardRequest
  ): Promise<GenerateControlCardResponse> {
    return this.execute({
      ...request,
      options: {
        ...request.options,
        finalize: false, // Preview mode (override finalize)
      },
    })
  }

  /**
   * Finalize control card (assign report number, no watermark)
   */
  async finalize(
    request: GenerateControlCardRequest
  ): Promise<GenerateControlCardResponse> {
    return this.execute({
      ...request,
      options: {
        ...request.options,
        finalize: true, // Finalize mode
      },
    })
  }

  /**
   * Get repository cache statistics
   */
  getCacheStats(): {
    templates: number
    references: { manufacturers: number; standards: number; total: number }
  } {
    return this.repository.getCacheStats()
  }

  /**
   * Clear repository caches
   */
  clearCaches(): void {
    this.repository.clearTemplateCache()
  }
}
